/**
 * NiV "שגיאות מערכת" tab — client error logs (niv_error_logs) with
 * per-code breakdown, expandable cards, handled/open status and deletion.
 *
 * Data flows through nivApi (getErrorLogs + mutations) so the dashboard shell
 * can share the same cache for its nav badge. Error codes: CK-7919/7929/7931/7932.
 */

import { useMemo, useState } from "react";
import {
  ArrowClockwise,
  Bug,
  Buildings,
  CaretDown,
  Check,
  CheckCircle,
  Copy,
  Trash,
} from "@phosphor-icons/react";
import ContentLoader from "../../../../components/shared/ContentLoader";
import LoaderLoop from "../../../../components/shared/loader/LoaderLoop";
import {
  useGetErrorLogsQuery,
  useUpdateErrorLogStatusMutation,
  useDeleteErrorLogMutation,
  useDeleteAllErrorLogsMutation,
  useGetNivClinicsOverviewQuery,
} from "../../../../store/services/nivApi";
import { useToast } from "../../../../contexts/ToastContext";
import ConfirmDialog from "../../../../components/ui/ConfirmDialog";

// Map a CK-NNNN error code to its module label (per the error-code registry)
const CK_MODULE_LABELS = {
  1: "אימות / הרשמה",
  2: "מטופלים",
  3: "יומן / פגישות / כספים",
  4: "משימות ותזכורות",
  5: "הגדרות קליניקה",
  6: "עמודים ציבוריים",
  7: "שירותי נתונים (RTK)",
  8: "Firebase",
  9: "Cloud Functions",
};

export const getErrorModule = (errorCode) => {
  if (!errorCode) return "לא ידוע";
  const m = String(errorCode).match(/CK-(\d)/);
  return (m && CK_MODULE_LABELS[Number(m[1])]) || "אחר";
};

export const ERROR_LOG_DAYS_OPTIONS = [
  { value: 1, label: "24 שעות אחרונות" },
  { value: 3, label: "3 ימים אחרונים" },
  { value: 7, label: "7 ימים אחרונים" },
  { value: 30, label: "30 ימים אחרונים" },
];

export const DEFAULT_ERROR_LOG_DAYS = 7;

// Quick filters for the messaging router's server-written rows
// (whatsapp_integration.md §2.8): WhatsApp→SMS fallbacks vs. nothing delivered.
const CODE_FILTERS = [
  { id: "all", label: "הכל" },
  { id: "fallback", label: "גיבוי SMS", codes: ["CK-9265", "CK-9266"], tone: "warn" },
  { id: "failed_both", label: "נכשל בשני הערוצים", codes: ["CK-9267"], tone: "bad" },
  { id: "server", label: "שרת", tone: "muted" },
];

const fmt = (iso, withSeconds) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("he-IL", {
    timeZone: "Asia/Jerusalem",
    day: "numeric",
    month: "short",
    ...(withSeconds ? { year: "numeric", second: "2-digit" } : {}),
    hour: "2-digit",
    minute: "2-digit",
  });
};

function NivErrorLogsTab({ isAuthorized, days, onDaysChange }) {
  const { showSuccess, showError } = useToast();
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const [confirm, setConfirm] = useState(null); // { type: "one", id } | { type: "all" }
  const [codeFilter, setCodeFilter] = useState("all");

  const { data: allErrorLogs = [], isLoading } = useGetErrorLogsQuery(
    { days },
    { skip: !isAuthorized }
  );
  const filterCounts = useMemo(() => {
    const c = {};
    CODE_FILTERS.forEach((f) => {
      if (f.id === "all") return;
      c[f.id] = allErrorLogs.filter((e) => (f.codes ? f.codes.includes(e.errorCode) : e.source === "server")).length;
    });
    return c;
  }, [allErrorLogs]);
  const errorLogs = useMemo(() => {
    const f = CODE_FILTERS.find((x) => x.id === codeFilter);
    if (!f || f.id === "all") return allErrorLogs;
    return allErrorLogs.filter((e) => (f.codes ? f.codes.includes(e.errorCode) : e.source === "server"));
  }, [allErrorLogs, codeFilter]);
  const { data: clinicRows = [] } = useGetNivClinicsOverviewQuery(undefined, {
    skip: !isAuthorized,
  });
  const [setStatus] = useUpdateErrorLogStatusMutation();
  const [deleteOne, { isLoading: deletingOne }] = useDeleteErrorLogMutation();
  const [deleteAll, { isLoading: deletingAll }] = useDeleteAllErrorLogsMutation();

  const clinicNameMap = useMemo(() => {
    const map = {};
    clinicRows.forEach((c) => {
      if (c.clinicId) map[c.clinicId] = c.name;
    });
    return map;
  }, [clinicRows]);

  const byCode = useMemo(
    () =>
      Object.entries(
        errorLogs.reduce((acc, item) => {
          const key = item.errorCode || "UNKNOWN";
          acc[key] = (acc[key] || 0) + 1;
          return acc;
        }, {})
      ).sort((a, b) => b[1] - a[1]),
    [errorLogs]
  );

  const toggle = (id) =>
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const handleSetStatus = async (id, status) => {
    try {
      await setStatus({ errorId: id, status }).unwrap();
      showSuccess(status === "handled" ? "השגיאה סומנה כטופלה" : "השגיאה סומנה כפתוחה");
    } catch {
      showError("שגיאה בעדכון הסטטוס");
    }
  };

  const handleConfirm = async () => {
    if (!confirm) return;
    try {
      if (confirm.type === "all") {
        const { deleted } = await deleteAll().unwrap();
        showSuccess(`נמחקו ${deleted} רשומות שגיאה`);
      } else {
        await deleteOne({ errorId: confirm.id }).unwrap();
        showSuccess("השגיאה נמחקה");
      }
    } catch {
      showError("שגיאה במחיקה");
    } finally {
      setConfirm(null);
    }
  };

  return (
    <div className="niv-error-logs-content">
      <div className="niv-error-logs-header">
        <h3>
          <Bug size={22} weight="fill" />
          שגיאות מערכת
        </h3>
        <div className="niv-help-analytics-filters">
          <select
            value={days}
            onChange={(e) => onDaysChange(Number(e.target.value))}
            className="niv-analytics-days-select"
            aria-label="טווח זמן"
          >
            {ERROR_LOG_DAYS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="niv-btn niv-btn-danger"
            onClick={() => setConfirm({ type: "all" })}
            disabled={deletingAll || allErrorLogs.length === 0}
          >
            {deletingAll ? (
              <>
                <LoaderLoop size="xs" /> מוחק...
              </>
            ) : (
              <>
                <Trash size={16} /> מחק את כל השגיאות
              </>
            )}
          </button>
        </div>
      </div>

      {!isLoading && allErrorLogs.length > 0 && (
        <div className="niv-error-code-filters" role="group" aria-label="סינון לפי סוג">
          {CODE_FILTERS.map((f) => (
            <button
              key={f.id}
              type="button"
              className={`niv-error-code-filter ${codeFilter === f.id ? "active" : ""} ${f.tone ? `tone-${f.tone}` : ""}`}
              onClick={() => setCodeFilter(f.id)}
            >
              {f.label}
              {f.id !== "all" && <span className="niv-error-code-filter-count">{filterCounts[f.id] || 0}</span>}
            </button>
          ))}
        </div>
      )}

      {isLoading ? (
        <ContentLoader message="טוען שגיאות..." />
      ) : errorLogs.length === 0 ? (
        <div className="niv-empty-state">
          <CheckCircle size={48} weight="light" />
          <h4>הכל תקין!</h4>
          <p>אין שגיאות בתקופה הנבחרת</p>
        </div>
      ) : (
        <>
          <div className="niv-help-analytics-summary">
            <div className="niv-support-stat urgent">
              <span className="niv-stat-value">{errorLogs.length}</span>
              <span className="niv-stat-label">סה"כ שגיאות</span>
            </div>
            <div className="niv-support-stat">
              <span className="niv-stat-value">{byCode.length}</span>
              <span className="niv-stat-label">קודי שגיאה ייחודיים</span>
            </div>
            <div className="niv-support-stat">
              <span className="niv-stat-value">
                {new Set(errorLogs.map((e) => e.clinicId)).size}
              </span>
              <span className="niv-stat-label">קליניקות מושפעות</span>
            </div>
            <div className="niv-support-stat">
              <span className="niv-stat-value">
                {errorLogs.filter((e) => e.status !== "handled").length}
              </span>
              <span className="niv-stat-label">טרם טופלו</span>
            </div>
          </div>

          <div className="niv-help-analytics-breakdown">
            <h4>לפי קוד שגיאה</h4>
            <div className="niv-help-feature-list">
              {byCode.map(([code, count]) => (
                <div key={code} className="niv-help-feature-row">
                  <span className="niv-help-feature-name niv-error-code">
                    <Bug size={14} weight="bold" />
                    {code}
                    <small className="niv-error-module-hint">{getErrorModule(code)}</small>
                  </span>
                  <span className="niv-help-feature-count niv-error-count">{count} שגיאות</span>
                </div>
              ))}
            </div>
          </div>

          <div className="niv-error-cards-wrap">
            <h4>שגיאות אחרונות</h4>
            {errorLogs.length > 200 && (
              <p className="niv-error-cards-note">
                מוצגות 200 שגיאות אחרונות מתוך {errorLogs.length}
              </p>
            )}
            <div className="niv-error-cards">
              {errorLogs.slice(0, 200).map((item) => {
                const isOpen = expandedIds.has(item.id);
                const isHandled = item.status === "handled";
                const clinicName =
                  item.clinicName || clinicNameMap[item.clinicId] || item.clinicId || "לא ידוע";
                const exactTime = fmt(item.occurredAt, true);
                const shortTime = fmt(item.occurredAt, false);
                return (
                  <div
                    key={item.id}
                    className={`niv-error-card ${isOpen ? "open" : ""} ${isHandled ? "handled" : ""}`}
                  >
                    <button
                      type="button"
                      className="niv-error-card-head"
                      onClick={() => toggle(item.id)}
                      aria-expanded={isOpen}
                    >
                      <span className={`niv-error-severity-dot ${item.severity && item.severity !== "error" ? `is-${item.severity}` : ""}`} />
                      <code className="niv-error-code-badge">{item.errorCode || "—"}</code>
                      {item.source === "server" && <span className="niv-error-source-pill">שרת</span>}
                      {item.meta?.messageType && (
                        <span className="niv-error-source-pill niv-error-meta-pill" dir="ltr">
                          {item.meta.messageType}
                          {item.meta.reason ? ` · ${item.meta.reason}` : ""}
                          {item.meta.metaErrorCode != null ? ` · ${item.meta.metaErrorCode}` : ""}
                        </span>
                      )}
                      <span className="niv-error-head-message">{item.errorMessage || "—"}</span>
                      {isHandled && (
                        <span className="niv-error-status-pill">
                          <CheckCircle size={12} weight="fill" />
                          טופל
                        </span>
                      )}
                      <span className="niv-error-head-clinic">
                        <Buildings size={14} />
                        {clinicName}
                      </span>
                      <span className="niv-error-head-time">{shortTime}</span>
                      <CaretDown size={18} weight="bold" className="niv-error-caret" />
                    </button>

                    {isOpen && (
                      <div className="niv-error-card-body">
                        <div className="niv-error-meta-grid">
                          <div className="niv-error-meta">
                            <span className="niv-error-meta-label">קליניקה</span>
                            <span className="niv-error-meta-value">
                              {clinicName}
                              {item.clinicId && (
                                <em className="niv-error-meta-id"> · {item.clinicId}</em>
                              )}
                            </span>
                          </div>
                          <div className="niv-error-meta">
                            <span className="niv-error-meta-label">מטפל</span>
                            <span className="niv-error-meta-value">{item.therapistName || "—"}</span>
                          </div>
                          <div className="niv-error-meta">
                            <span className="niv-error-meta-label">קוד ומודול</span>
                            <span className="niv-error-meta-value">
                              {item.errorCode || "—"} · {getErrorModule(item.errorCode)}
                            </span>
                          </div>
                          <div className="niv-error-meta">
                            <span className="niv-error-meta-label">נתיב</span>
                            <span className="niv-error-meta-value niv-error-mono">{item.route || "—"}</span>
                          </div>
                          <div className="niv-error-meta">
                            <span className="niv-error-meta-label">מזהה מטופל</span>
                            <span className="niv-error-meta-value niv-error-mono">{item.patientId || "—"}</span>
                          </div>
                          <div className="niv-error-meta">
                            <span className="niv-error-meta-label">זמן מדויק</span>
                            <span className="niv-error-meta-value">{exactTime}</span>
                          </div>
                          <div className="niv-error-meta niv-error-meta-wide">
                            <span className="niv-error-meta-label">User-Agent</span>
                            <span className="niv-error-meta-value niv-error-mono">{item.userAgent || "—"}</span>
                          </div>
                          <div className="niv-error-meta niv-error-meta-wide">
                            <span className="niv-error-meta-label">מזהה רשומה</span>
                            <span className="niv-error-meta-value niv-error-mono">{item.id}</span>
                          </div>
                        </div>

                        <div className="niv-error-detail-block">
                          <div className="niv-error-detail-head">
                            <span>הודעת שגיאה מלאה</span>
                            <button
                              type="button"
                              className="niv-error-copy-btn"
                              onClick={() => {
                                const dump = [
                                  `Code: ${item.errorCode || "—"}`,
                                  `Module: ${getErrorModule(item.errorCode)}`,
                                  `Clinic: ${clinicName} (${item.clinicId || "—"})`,
                                  `Therapist: ${item.therapistName || "—"}`,
                                  `Route: ${item.route || "—"}`,
                                  `Time: ${exactTime}`,
                                  `Message: ${item.errorMessage || "—"}`,
                                  `Stack: ${item.stack || "—"}`,
                                ].join("\n");
                                navigator.clipboard?.writeText(dump);
                                showSuccess("הפרטים הועתקו");
                              }}
                            >
                              <Copy size={14} />
                              העתק
                            </button>
                          </div>
                          <pre className="niv-error-pre">{item.errorMessage || "—"}</pre>
                        </div>

                        {item.stack && (
                          <div className="niv-error-detail-block">
                            <div className="niv-error-detail-head">
                              <span>Stack Trace</span>
                            </div>
                            <pre className="niv-error-pre niv-error-stack">{item.stack}</pre>
                          </div>
                        )}

                        {item.context && (
                          <div className="niv-error-detail-block">
                            <div className="niv-error-detail-head">
                              <span>Context</span>
                            </div>
                            <pre className="niv-error-pre">{item.context}</pre>
                          </div>
                        )}

                        <div className="niv-error-card-actions">
                          {isHandled ? (
                            <button
                              type="button"
                              className="niv-btn niv-btn-sm niv-btn-secondary"
                              onClick={() => handleSetStatus(item.id, "open")}
                            >
                              <ArrowClockwise size={14} />
                              פתח מחדש
                            </button>
                          ) : (
                            <button
                              type="button"
                              className="niv-btn niv-btn-sm niv-btn-success"
                              onClick={() => handleSetStatus(item.id, "handled")}
                            >
                              <Check size={14} />
                              סמן כטופל
                            </button>
                          )}
                          <button
                            type="button"
                            className="niv-btn-icon danger"
                            onClick={() => setConfirm({ type: "one", id: item.id })}
                            disabled={deletingOne}
                            title="מחק"
                            aria-label="מחק שגיאה"
                          >
                            <Trash size={16} />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}

      <ConfirmDialog
        isOpen={!!confirm}
        onClose={() => setConfirm(null)}
        onConfirm={handleConfirm}
        title={confirm?.type === "all" ? "מחיקת כל השגיאות" : "מחיקת רשומת שגיאה"}
        message={
          confirm?.type === "all"
            ? "כל רשומות השגיאות במערכת יימחקו לצמיתות. פעולה זו אינה הפיכה."
            : "רשומת השגיאה תימחק לצמיתות."
        }
        confirmText="מחק"
        cancelText="ביטול"
        type="danger"
        icon="delete"
      />
    </div>
  );
}

export default NivErrorLogsTab;
